import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useDeviceType } from '@cian/ui-kit';

import { MainNews } from '../../components/MainNews';
import { selectMainNews } from '../../selectors/mainNews';
import { selectMainArticles, selectTopMainArticle } from '../../selectors/mainArticles';
import { MainArticleDetailed, MainArticleSimplified } from '../../components/MainArticle';
import { TThunkDispatch } from '../../types/redux';
import { getMainNews, resetMainNews } from '../../actions/mainNews';
import { ERequestStatus } from '../../types/requestStatus';
import { IArticlesMainAttributesRubricSchema } from '../../repositories/journal/entities/journal/ArticlesMainAttributesRubricSchema';
import { MAIN_NEWS_REQUEST_LIMIT } from '../../constants';
import { EType } from '../../repositories/journal/entities/journal/JournalListAttributesSchema';
import { buildPostUrl, useTimeoutEffect } from '../../utils';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { Skeleton } from '../../components/Skeleton';
import * as s from './MainArticleAndMainNewsContainer.css';

/** Главная статья и блок новостей на Главной */
export const MainArticleAndMainNewsContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { search } = useLocation();
  const [withTag, setWithTag] = useState(new URLSearchParams(search).get('with-tag') || '');
  const history = useHistory();
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const { items: mainNews, status: mainNewsStatus } = useSelector(selectMainNews);
  const mainArticle = useSelector(selectTopMainArticle);
  const appendType = useSelector(selectListingAppendType);
  const { status: articleListingStatus } = useSelector(selectMainArticles);
  const [isArticleSkeletonShown, setIsArticleSkeletonShown] = useState(
    articleListingStatus === ERequestStatus.Loading && appendType === 'set',
  );
  const [isNewsSkeletonShown, setIsNewsSkeletonShown] = useState(
    mainNewsStatus === ERequestStatus.Loading && appendType === 'set',
  );

  /**
   * Запрашиваем список новостей, если еще ни разу не запрашивали (1)
   * или сменился with-tag в qs (2)
   */
  useEffect(() => {
    const qsWithTag = new URLSearchParams(search).get('with-tag') || '';

    if (qsWithTag !== withTag || mainNewsStatus === ERequestStatus.Initial) {
      dispatch(getMainNews({ limit: MAIN_NEWS_REQUEST_LIMIT, tag: qsWithTag })).finally();
    }

    if (qsWithTag !== withTag) {
      setWithTag(qsWithTag);
    }
  }, [dispatch, mainNewsStatus, search, withTag]);

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsArticleSkeletonShown(articleListingStatus === ERequestStatus.Loading && appendType === 'set');
  }, [!isArticleSkeletonShown || mainArticle ? 0 : 500]);

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsNewsSkeletonShown(mainNewsStatus === ERequestStatus.Loading && appendType === 'set');
  }, [!isNewsSkeletonShown || mainNews?.length ? 0 : 500]);

  /** При выходе сбрасывает стор новостей */
  useEffect(() => {
    return () => {
      dispatch(resetMainNews());
    };
  }, [dispatch]);

  /** Роутит по клику по рубрике */
  const handleRubricButtonClick = (url: IArticlesMainAttributesRubricSchema['url']) => {
    history.push(url);
  };

  /** Роутит по клику по статье */
  const handleArticleClick = (url: string) => {
    history.push(url);
  };

  if (!mainArticle && !isArticleSkeletonShown) {
    return null;
  }

  return (
    <div className={s['wrapper']}>
      <div className={s['main-article-wrapper']}>
        {!isArticleSkeletonShown &&
          (isPhone() ? (
            <MainArticleSimplified
              title={mainArticle?.attributes?.title || ''}
              image={mainArticle?.attributes.image}
              url={buildPostUrl({
                type: EType.Articles,
                slug: mainArticle?.attributes.slug || '',
                id: mainArticle?.id || 0,
              })}
              onClick={() =>
                handleArticleClick(
                  buildPostUrl({
                    type: EType.Articles,
                    slug: mainArticle?.attributes.slug || '',
                    id: mainArticle?.id || 0,
                  }),
                )
              }
            />
          ) : (
            <MainArticleDetailed
              title={mainArticle?.attributes.title || ''}
              titleColor={mainArticle?.attributes.titleColor || undefined}
              image={mainArticle?.attributes.image}
              rubric={mainArticle?.attributes.rubrics?.length ? mainArticle.attributes.rubrics[0] : undefined}
              viewsCount={mainArticle?.attributes.numViews || 0}
              commentsCount={mainArticle?.attributes.commentsCount || 0}
              articleUrl={buildPostUrl({
                type: EType.Articles,
                slug: mainArticle?.attributes.slug || '',
                id: mainArticle?.id || 0,
              })}
              onClick={() =>
                handleArticleClick(
                  buildPostUrl({
                    type: EType.Articles,
                    slug: mainArticle?.attributes.slug || '',
                    id: mainArticle?.id || 0,
                  }),
                )
              }
              onRubricButtonClick={() => handleRubricButtonClick(mainArticle?.attributes?.rubrics[0]?.url || '')}
            />
          ))}

        {/* Прелоадер */}
        {isArticleSkeletonShown && <Skeleton height={isPhone() ? '50vw' : '100%'} />}
      </div>

      {isNewsSkeletonShown ? (
        <Skeleton width={'100%'} height={isPhone() ? '50vw' : 409} />
      ) : (
        <MainNews items={mainNews} />
      )}
    </div>
  );
};
